import Box from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { BoxOwnProps } from '@mui/system/Box';
import {
  InvoiceCategoryFilter,
  InvoiceState as InvoiceStateEnums,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import React, { useState } from 'react';

import { GridCardWrapper } from '~components/Cards/GridCardWrapper';
import { SimpleCard } from '~components/Cards/SimpleCard';
import InvoiceState from '~components/Invoice/InvoiceState';
import { RouteMap } from '~components/Job/RouteMap';
import { SmallTabs } from '~components/Tabs/SmallTabs';
import { Invoice } from '~hooks/useInvoices/models';
import useInvoices from '~hooks/useInvoices/useInvoices';
import { Job } from '~hooks/useJob/models';
import { InfoIcon } from '~icons/InfoIcon';
import { DriverPayUtils } from '~pages/Approvals/DriverPay/driverPayUtils';
import LineItemsTable from '~pages/Approvals/DriverPay/LineItemsTable';
import VendorRatePanel from '~pages/Approvals/DriverPay/VendorRatePanel';
import { useStores } from '~store';
import { InvoiceSearchParamName } from '~store/InvoicesStore';

import { CommonLoader } from '../Loaders/CommonLoader';

interface InvoicesTabContentProps {
  customCategory?: InvoiceCategoryFilter;
  customHeader?: React.ReactNode;
  hideMap?: boolean;
  invoice: Invoice;
  isDayDetailsView?: boolean;
  job: Job;
  reload?: () => void;
  utils: DriverPayUtils;
}

export default function InvoicesTabContent({
  customCategory,
  customHeader,
  hideMap,
  invoice,
  isDayDetailsView = false,
  job,
  reload,
  utils,
}: InvoicesTabContentProps) {
  const theme = useTheme();
  const { invoicesStore } = useStores();
  const currentCategory =
    invoicesStore.searchParams[InvoiceSearchParamName.INVOICE_CATEGORY];
  const [editingComponent, setEditingComponent] = useState<'vendor' | 'lineItems' | null>(
    null,
  );

  // Shows readonly details based on the invoice state if the invoice is not actionable
  const showDriverDayStateBasedDetails =
    isDayDetailsView && !invoice.editable && !invoice.approvable && !invoice.unapprovable;

  const { isLoading, isUpdating } = useInvoices();

  const handleEditChange =
    (componentType: 'vendor' | 'lineItems') => (isEditing: boolean) => {
      if (isEditing) {
        setEditingComponent(componentType);
      } else {
        setEditingComponent(null);
      }
    };

  const DriverDayDetailsView = () => {
    if (
      [
        InvoiceStateEnums.APPROVED,
        InvoiceStateEnums.CUSTOMER_APPROVED,
        InvoiceStateEnums.CUSTOMER_PAID,
        InvoiceStateEnums.PAID,
      ].includes(invoice.state)
    ) {
      return (
        <Box
          sx={{
            mt: 2,
          }}
        >
          <InvoiceState state={invoice.state} />
        </Box>
      );
    } else {
      return (
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            alignItems: 'center',
            flex: 1,
            mt: 2,
          }}
        >
          <InfoIcon />
          <Typography
            color={theme.brandV2.colors.treadBlack}
            className="MuiTypography-body1"
            sx={{
              fontWeight: 400,
            }}
          >
            {t('approvals.driver_day.approval_and_editing_not_allowed')}
          </Typography>
        </Box>
      );
    }
  };

  return isLoading || isUpdating ? (
    <CommonLoader />
  ) : (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
        }}
      >
        <GridCardWrapper>
          <SimpleCard>
            <Box
              sx={{
                m: -2,
              }}
            >
              {isDayDetailsView ? customHeader : null}
              <CardPanel
                hasBorderBottom
                sx={{
                  ...(showDriverDayStateBasedDetails && {
                    display: 'flex',
                    justifyContent: 'space-between',
                  }),
                }}
              >
                <VendorRatePanel
                  invoice={invoice}
                  job={job}
                  utils={utils}
                  category={
                    (isDayDetailsView ? customCategory : currentCategory) ||
                    InvoiceCategoryFilter.PAYABLES
                  }
                  canEdit={editingComponent !== 'lineItems'}
                  onEditChange={handleEditChange('vendor')}
                  reload={reload}
                />
                {showDriverDayStateBasedDetails ? <DriverDayDetailsView /> : null}
              </CardPanel>

              <CardPanel>
                <LineItemsTable
                  invoice={invoice}
                  job={job}
                  utils={utils}
                  canEdit={editingComponent !== 'vendor'}
                  onEditChange={handleEditChange('lineItems')}
                />
              </CardPanel>
            </Box>
          </SimpleCard>
        </GridCardWrapper>
      </Box>

      {hideMap ? null : (
        <Box
          sx={{
            minWidth: '500px',
            flex: 0,
          }}
        >
          <SmallTabs
            tabs={[
              {
                label: 'Map',
                id: `map_${job.id}`,
                content: (
                  <Box sx={{ m: -2 }}>
                    <RouteMap job={job} />
                  </Box>
                ),
              },
            ]}
          />
        </Box>
      )}
    </Box>
  );
}

interface CardPanelProps extends BoxOwnProps {
  hasBorderBottom?: boolean;
}

const CardPanel = styled(Box, {
  shouldForwardProp: (propName) => propName !== 'hasBorderBottom',
})<CardPanelProps>(({ theme, hasBorderBottom }) => ({
  '&.MuiBox-root': {
    padding: theme.spacing(2),
    ...(hasBorderBottom ? { borderBottom: `solid 1px ${theme.palette.divider}` } : {}),
  },
}));
